// src/components/mitchRoom/revenueAnalytics/PacingPanel.js
//
// "How are we doing this month vs last year" — and, since Aug 2026, "where
// will the month land". Three states, driven by the month strip:
//   closed  — final vs last year
//   current — live MTD, the same elapsed days last year, and the forecast
//   future  — forecast only (last year's shape for reference)
//
// Two things make the YoY comparison honest, and both are easy to get wrong:
//  1. Month-to-date is compared against the SAME number of elapsed days last
//     year, never against last year's full month.
//  2. August is aligned by distance to that season's GW1 deadline (FPL
//     revenue tracks the deadline, which moved from 15 Aug 2025 to 21 Aug
//     2026). Every other month is one renewal batch plus stragglers, and a
//     calendar month holds the whole batch both years, so those compare
//     calendar-to-calendar (the API's 'auto' alignment).
//
// The forecast comes from the pipeline's subscription-book model (see
// docs/revenue-forecast.md), not from rescaling last year's curve; the old
// shape projection is kept only as a secondary figure for August.
//
// Future DAYS come from the pipeline too (`fc_*` on each row): renewals on
// the day each bill is due, new business on last year's new-business shape.
// They used to be the month forecast spread over LAST year's daily shape,
// which drew this season's renewal step on last season's bill day (the 16th
// instead of the 21st). The alignment toggle only moves the last-year line.
import React, { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Info } from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { Switch } from 'components/ui/switch';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import { Badge } from 'components/ui/badge';
import { cn } from 'lib/utils';
import { formatCurrency, formatPercent, formatAxisCurrency, formatPeriod } from 'utils/format';
import ForecastBridge from './ForecastBridge';

// Buyer classes, in the order they stack. Colours from the validated
// dataviz palette; one colour per class, reused everywhere on the page.
export const BUYER_CLASSES = [
  { key: 'bc_new', label: 'New customers', color: '#1baf7a' },
  { key: 'bc_winback', label: 'Win-back', color: '#2a78d6' },
  { key: 'bc_expansion', label: 'Expansion', color: '#eb6834' },
  { key: 'bc_renewal', label: 'Recurring', color: '#eda100' },
  { key: 'bc_unattributed', label: 'Unattributed', color: '#9e9e9e' },
];

const LY_COLOR = '#9e9e9e';
const FORECAST_COLOR = '#254A73';
// Forecast bars reuse the buyer-class colours, lighter: scheduled renewals
// in the Recurring colour, modelled new business in the New-customers one.
const FC_RENEWAL_COLOR = '#eda100';
const FC_NEW_COLOR = '#1baf7a';
const FC_BAR_OPACITY = 0.4;

// 371 → "last season's fell 6 days earlier in the calendar"; 364 → "1 day later"
function describeShift(shiftDays) {
  const diff = (shiftDays ?? 365) - 365;
  if (diff === 0) return "last season's fell on the same calendar day";
  const n = Math.abs(diff);
  return `last season's fell ${n} day${n === 1 ? '' : 's'} ${diff > 0 ? 'earlier' : 'later'} in the calendar`;
}

function Delta({ value, className }) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  const good = value >= 0;
  const Icon = good ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-token-sm font-medium',
        good ? 'text-success' : 'text-destructive',
        className
      )}
    >
      <Icon size={13} aria-hidden="true" />
      {good ? '+' : ''}
      {value.toFixed(1)}%
    </span>
  );
}

function PaceCard({ label, value, sub, delta, tooltip, accent, large, badge }) {
  return (
    <div
      className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
      style={{ borderLeftColor: accent }}
      title={tooltip}
    >
      <div className="mb-1 flex items-center gap-1">
        <span className="whitespace-nowrap text-token-sm text-muted-foreground">{label}</span>
        {tooltip && <Info size={11} className="shrink-0 text-muted-foreground" aria-hidden="true" />}
        {badge && (
          <Badge variant="info" className="ml-auto px-1.5 py-0 text-[10px]">
            {badge}
          </Badge>
        )}
      </div>
      <p className={cn('font-semibold', large ? 'text-token-2xl' : 'text-token-lg')}>{value}</p>
      <div className="mt-0.5 flex items-center gap-2">
        {sub && <span className="text-token-xs text-muted-foreground">{sub}</span>}
        <Delta value={delta} className="text-token-xs" />
      </div>
    </div>
  );
}

export default function PacingPanel({ pacing, forecastMonth, loading, align, onAlignChange }) {
  const [cumulative, setCumulative] = useState(true);
  const [stackByClass, setStackByClass] = useState(true);

  const summary = pacing?.summary;
  const daily = pacing?.daily;
  const isFutureMonth = Boolean(pacing?.is_future_month);
  const elapsedDays = useMemo(() => (daily || []).filter((d) => !d.is_future).length, [daily]);
  const isClosedMonth = !isFutureMonth && Array.isArray(daily) && daily.length > 0 && elapsedDays === daily.length;
  const fc = forecastMonth && forecastMonth.has_forecast ? forecastMonth : null;

  const hasDailyForecast = useMemo(
    () => Array.isArray(daily) && daily.some((d) => typeof d.fc_cum === 'number'),
    [daily]
  );

  // Extend the cumulative line with the forecast. With the pipeline's daily
  // forecast the line is live MTD + each day's expected revenue, so the
  // renewal batch steps on the day it is actually due. Without it (pipeline
  // not deployed yet) fall back to spreading the month forecast over last
  // year's remaining shape.
  const chartData = useMemo(() => {
    if (!Array.isArray(daily)) return [];
    const rows = daily.map((d) => ({ ...d, label: d.date }));
    if (isClosedMonth) return rows;
    if (hasDailyForecast) {
      const first = rows.findIndex((r) => typeof r.fc_cum === 'number');
      return rows.map((r, i) => {
        // anchor the dashed line on the last completed day's actual total
        if (i === first - 1 && typeof r.cumulative === 'number') {
          return { ...r, fc_cum: r.cumulative, fc_band: [r.cumulative, r.cumulative] };
        }
        if (typeof r.fc_cum !== 'number') return r;
        return { ...r, fc_band: [r.fc_cum_low ?? r.fc_cum, r.fc_cum_high ?? r.fc_cum] };
      });
    }
    if (!fc) return rows;
    const lastIdx = rows.map((r) => !r.is_future).lastIndexOf(true);
    const mtd = lastIdx >= 0 ? rows[lastIdx].cumulative ?? 0 : 0;
    const lyAt = lastIdx >= 0 ? rows[lastIdx].ly_cumulative ?? 0 : 0;
    const lyEnd = rows[rows.length - 1].ly_cumulative ?? 0;
    const lyRest = lyEnd - lyAt;
    const target = fc.forecast ?? mtd;
    const lo = fc.low ?? target;
    const hi = fc.high ?? target;
    return rows.map((r, i) => {
      if (i < lastIdx) return r;
      const frac = lyRest > 0 ? ((r.ly_cumulative ?? 0) - lyAt) / lyRest : (i - lastIdx) / Math.max(1, rows.length - 1 - lastIdx);
      const f = Math.min(1, Math.max(0, i === lastIdx ? 0 : frac));
      return {
        ...r,
        fc_cum: mtd + (target - mtd) * f,
        fc_band: [mtd + (lo - mtd) * f, mtd + (hi - mtd) * f],
      };
    });
  }, [daily, fc, isClosedMonth, hasDailyForecast]);

  const hasBuyerClass = useMemo(
    () =>
      Array.isArray(daily) &&
      daily.some((d) => BUYER_CLASSES.some((c) => (d[c.key] ?? 0) !== 0)),
    [daily]
  );

  if (loading && !pacing) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <Skeleton className="mb-3 h-6 w-64" />
        <Skeleton className="h-[320px] w-full" />
      </div>
    );
  }

  if (!pacing || !summary) {
    return (
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <h3 className="text-token-xl font-semibold">Pacing vs last year</h3>
        <p className="mt-2 text-token-md text-muted-foreground">No pacing data available.</p>
      </div>
    );
  }

  const monthLabel = formatPeriod(pacing.month);
  const deadlineAligned = pacing.align === 'deadline';
  const elapsed = elapsedDays;
  const isAugust = pacing.month.slice(5, 7) === '08';

  // Annotation lines that fall inside this month
  const inMonth = (pacing.annotations || []).filter((a) => a.date?.startsWith(pacing.month));
  const annotationLabel = (a) => {
    if (a.kind === 'deadline' || a.kind === 'deadline_ly') return a.kind === 'deadline_ly' ? 'GW1 (LY)' : 'GW1';
    if (a.kind === 'batch') return 'Batch';
    if (a.kind === 'batch_ly') return 'Batch (LY)';
    if (a.kind === 'price') return 'Price';
    if (a.kind === 'incident') return 'Incident';
    if (a.kind === 'billing') return 'Billing';
    return a.label?.includes('GW1') ? 'GW1' : 'Event';
  };

  const tooltipFormatter = (value, name) => {
    if (value === null || value === undefined) return [null, null];
    const cls = BUYER_CLASSES.find((c) => c.key === name);
    if (cls) return [formatCurrency(value), cls.label];
    const labels = {
      total: 'This year',
      ly_total: 'Last year (aligned)',
      cumulative: 'This year (cumulative)',
      ly_cumulative: 'Last year (cumulative)',
      fc_cum: 'Forecast (cumulative)',
      fc_band: 'Forecast low – high',
      fc_total: 'Forecast (still to come)',
      fc_renewals: 'Forecast: renewals due',
      fc_new: 'Forecast: new business',
    };
    if (Array.isArray(value)) return [`${formatCurrency(value[0], { whole: true })} – ${formatCurrency(value[1], { whole: true })}`, labels[name] || name];
    return [formatCurrency(value), labels[name] || name];
  };

  const lyFinal = fc ? (deadlineAligned ? fc.ly_aligned : fc.ly_calendar) : summary.ly_window_total;
  const fcVsLy = fc && lyFinal > 0 ? ((fc.forecast - lyFinal) / lyFinal) * 100 : null;
  const committedRemaining = fc ? fc.committed : null;
  const stateBadge = isFutureMonth ? 'forecast' : isClosedMonth ? 'final' : 'live';
  const showForecast = (fc || hasDailyForecast) && !isClosedMonth;
  const hasPlan = typeof summary.plan_expected === 'number' && summary.plan_days > 0;
  const planPartial = hasPlan && summary.plan_from && summary.plan_from.slice(8, 10) !== '01';

  return (
    <div className="flex flex-col gap-3">
      {/* Headline cards: Actual MTD · Committed · Forecast · vs LY same point · LY final */}
      <div className={cn('grid grid-cols-2 gap-3', hasPlan ? 'xl:grid-cols-6' : 'xl:grid-cols-5')}>
        <PaceCard
          label={isFutureMonth ? `${monthLabel} (not started)` : isClosedMonth ? `${monthLabel} final` : `${monthLabel} so far`}
          value={isFutureMonth ? '—' : formatCurrency(summary.mtd, { whole: true })}
          sub={isFutureMonth ? 'no revenue yet' : `${elapsed} day${elapsed === 1 ? '' : 's'}`}
          delta={isFutureMonth ? null : summary.pace_delta_pct}
          accent="#59A14F"
          large
          badge={stateBadge}
          tooltip={`Net of refunds. Compared with the same ${elapsed} days last year (${
            deadlineAligned ? 'deadline-aligned' : 'same calendar dates'
          }).`}
        />
        <PaceCard
          label="Committed (remaining)"
          value={committedRemaining !== null ? formatCurrency(committedRemaining, { whole: true }) : '—'}
          sub={fc ? `${fc.committed_subs} invoices → expected ${formatCurrency(fc.expected_renewals, { whole: true })}` : 'forecast not built yet'}
          accent="#1072CA"
          tooltip="Face value of invoices already scheduled in the rest of the month (excludes pending cancellations, paused and past-due). Expected = after the survival × collection haircut."
        />
        <PaceCard
          label={isClosedMonth ? 'Forecast (as built)' : 'Forecast'}
          value={
            fc
              ? formatCurrency(fc.forecast, { whole: true })
              : !isClosedMonth && isAugust && summary.projected_month
                ? formatCurrency(summary.projected_month, { whole: true })
                : '—'
          }
          sub={
            fc
              ? `${formatCurrency(fc.low, { whole: true })} – ${formatCurrency(fc.high, { whole: true })}`
              : isClosedMonth
                ? 'no forecast recorded for this month'
                : isAugust && summary.projected_month
                  ? 'LY-shape projection (no forecast table)'
                  : 'no forecast'
          }
          delta={fcVsLy}
          accent={FORECAST_COLOR}
          large
          tooltip="Banked + expected renewals + new business + tail of new − refunds, from the pipeline's subscription-book model. Band = low (1.5× churn, no new business) to high (0.5× churn, undamped YoY). Delta is vs last year's final for this month."
        />
        {hasPlan && (
          <PaceCard
            label="vs expected so far"
            value={`${summary.plan_delta_gbp >= 0 ? '+' : '−'}${formatCurrency(Math.abs(summary.plan_delta_gbp), { whole: true })}`}
            sub={`${formatCurrency(summary.plan_actual, { whole: true })} vs ${formatCurrency(summary.plan_expected, { whole: true })} expected${
              planPartial ? ` · since ${formatPeriod(summary.plan_from)}` : ''
            }`}
            delta={summary.plan_delta_pct}
            accent={FORECAST_COLOR}
            tooltip="Collected vs what the forecast expected for the same completed days (today excluded): renewals due on those days after the churn and collection haircuts, plus modelled new business. Unlike the last-year comparison, this is not thrown by the renewal batch falling on a different day this season."
          />
        )}
        <PaceCard
          label="Same point last year"
          value={isFutureMonth ? '—' : formatCurrency(summary.ly_same_point, { whole: true })}
          sub={isFutureMonth ? '' : `${summary.ly_same_point_orders} orders · ${summary.pace_delta_gbp >= 0 ? '+' : ''}${formatCurrency(summary.pace_delta_gbp, { whole: true })}`}
          accent={LY_COLOR}
          tooltip="Last year's revenue over the equivalent elapsed days — never the full month."
        />
        <PaceCard
          label="Last year final"
          value={formatCurrency(lyFinal, { whole: true })}
          sub={deadlineAligned ? 'deadline-aligned window' : 'calendar month'}
          accent="#B07AA1"
          tooltip={
            isAugust && summary.projected_month
              ? `LY-shape projection for reference: ${formatCurrency(summary.projected_month, { whole: true })}. Orders ${summary.mtd_orders} · AOV ${formatCurrency(summary.aov)} (LY ${formatCurrency(summary.ly_aov)}).`
              : `Orders ${summary.mtd_orders} · AOV ${formatCurrency(summary.aov)} (LY ${formatCurrency(summary.ly_aov)}).`
          }
        />
      </div>

      {/* Buyer-class summary: the new/returning/expansion split */}
      {hasBuyerClass && !isFutureMonth && (
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {BUYER_CLASSES.filter((c) => c.key !== 'bc_unattributed').map((c) => {
            const s = summary.buyer_class?.[c.key];
            if (!s) return null;
            const share = summary.mtd > 0 ? (s.mtd / summary.mtd) * 100 : 0;
            return (
              <div
                key={c.key}
                className="rounded-token-large border border-borderGrey border-l-4 bg-white p-3"
                style={{ borderLeftColor: c.color }}
              >
                <p className="text-token-sm text-muted-foreground">{c.label}</p>
                <p className="text-token-lg font-semibold">
                  {formatCurrency(s.mtd, { whole: true })}
                </p>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="text-token-xs text-muted-foreground">
                    {formatPercent(share, 0)} of MTD
                  </span>
                  <Delta value={s.delta_pct} className="text-token-xs" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bridge: why the forecast is what it is */}
      {fc && !isClosedMonth && <ForecastBridge month={fc} label={monthLabel} />}

      {/* Daily chart */}
      <div className="rounded-token-large border border-borderGrey bg-white p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-token-xl font-semibold">
              Daily revenue vs last year — {monthLabel}
            </h3>
            <p className="text-token-xs text-muted-foreground">
              {deadlineAligned
                ? `Aligned to the GW1 deadline (${describeShift(pacing.deadline_shift_days)})${
                    pacing.deadline_provisional ? ' · next season’s deadline is PROVISIONAL' : ''
                  }`
                : 'Aligned by calendar date'}
              {pacing.align_fallback_reason ? ` · ${pacing.align_fallback_reason}` : ''}
              {pacing.data_as_of ? ` · data to ${pacing.data_as_of.replace('T', ' ').replace('Z', ' UTC')}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={align} onValueChange={onAlignChange}>
              <TabsList>
                <TabsTrigger value="auto">Auto</TabsTrigger>
                <TabsTrigger value="deadline">Deadline-aligned</TabsTrigger>
                <TabsTrigger value="calendar">Calendar</TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Switch id="pacing-cumulative" checked={cumulative} onCheckedChange={setCumulative} />
              <Label htmlFor="pacing-cumulative" className="cursor-pointer text-token-base font-normal">
                Cumulative
              </Label>
            </div>
            {hasBuyerClass && !cumulative && (
              <div className="flex items-center gap-2">
                <Switch
                  id="pacing-stack"
                  checked={stackByClass}
                  onCheckedChange={setStackByClass}
                />
                <Label htmlFor="pacing-stack" className="cursor-pointer text-token-base font-normal">
                  Split by buyer
                </Label>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {!cumulative && stackByClass && hasBuyerClass ? (
            BUYER_CLASSES.map((c) => (
              <span
                key={c.key}
                className="rounded-token-large px-2.5 py-0.5 text-xs font-medium text-white"
                style={{ backgroundColor: c.color }}
              >
                {c.label}
              </span>
            ))
          ) : (
            <span
              className="rounded-token-large px-2.5 py-0.5 text-xs font-medium text-white"
              style={{ backgroundColor: '#1072CA' }}
            >
              This year
            </span>
          )}
          <span
            className="rounded-token-large border border-dashed px-2.5 py-0.5 text-xs font-medium"
            style={{ borderColor: LY_COLOR, color: LY_COLOR }}
          >
            Last year (aligned)
          </span>
          {cumulative && showForecast && (
            <span
              className="rounded-token-large border border-dashed px-2.5 py-0.5 text-xs font-medium"
              style={{ borderColor: FORECAST_COLOR, color: FORECAST_COLOR }}
            >
              Forecast (band = low–high)
            </span>
          )}
          {!cumulative && hasDailyForecast && !isClosedMonth && (
            <>
              <span
                className="rounded-token-large px-2.5 py-0.5 text-xs font-medium text-white"
                style={{ backgroundColor: FC_RENEWAL_COLOR, opacity: 0.65 }}
              >
                Forecast: renewals due
              </span>
              <span
                className="rounded-token-large px-2.5 py-0.5 text-xs font-medium text-white"
                style={{ backgroundColor: FC_NEW_COLOR, opacity: 0.65 }}
              >
                Forecast: new business
              </span>
            </>
          )}
        </div>

        <div className="h-[340px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#DADADA" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                tickFormatter={(d) => formatPeriod(d)}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis tickFormatter={formatAxisCurrency} tick={{ fontSize: 11 }} width={58} />
              <Tooltip
                formatter={tooltipFormatter}
                labelFormatter={(label, payload) => {
                  const row = payload?.[0]?.payload;
                  const base = formatPeriod(label);
                  const partial = row?.is_partial ? ' (today — partial)' : row?.is_future ? ' (forecast)' : '';
                  return row?.ly_date
                    ? `${base}${partial} · vs ${formatPeriod(row.ly_date)}`
                    : `${base}${partial}`;
                }}
              />

              {inMonth.map((a) => (
                <ReferenceLine
                  key={`${a.kind || 'ev'}-${a.date}-${a.label}`}
                  x={a.date}
                  stroke={a.kind === 'incident' ? '#E15759' : a.kind?.endsWith('_ly') ? LY_COLOR : '#254A73'}
                  strokeDasharray="4 3"
                  label={{
                    value: annotationLabel(a),
                    position: 'top',
                    fontSize: 10,
                    fill: a.kind === 'incident' ? '#E15759' : a.kind?.endsWith('_ly') ? LY_COLOR : '#254A73',
                  }}
                />
              ))}

              {cumulative ? (
                <>
                  {showForecast && (
                    <Area
                      type="linear"
                      dataKey="fc_band"
                      stroke="none"
                      fill={FORECAST_COLOR}
                      fillOpacity={0.12}
                      isAnimationActive={false}
                      connectNulls={false}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="ly_cumulative"
                    stroke={LY_COLOR}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    isAnimationActive={false}
                  />
                  {showForecast && (
                    <Line
                      type="linear"
                      dataKey="fc_cum"
                      stroke={FORECAST_COLOR}
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={false}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="cumulative"
                    stroke="#1072CA"
                    strokeWidth={2.5}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </>
              ) : (
                <>
                  {stackByClass && hasBuyerClass ? (
                    BUYER_CLASSES.map((c) => (
                      <Bar
                        key={c.key}
                        dataKey={c.key}
                        stackId="buyer"
                        fill={c.color}
                        isAnimationActive={false}
                      />
                    ))
                  ) : (
                    <Bar dataKey="total" stackId="buyer" fill="#1072CA" isAnimationActive={false} />
                  )}
                  {hasDailyForecast && !isClosedMonth && (
                    <Bar
                      dataKey="fc_renewals"
                      stackId="buyer"
                      fill={FC_RENEWAL_COLOR}
                      fillOpacity={FC_BAR_OPACITY}
                      isAnimationActive={false}
                    />
                  )}
                  {hasDailyForecast && !isClosedMonth && (
                    <Bar
                      dataKey="fc_new"
                      stackId="buyer"
                      fill={FC_NEW_COLOR}
                      fillOpacity={FC_BAR_OPACITY}
                      isAnimationActive={false}
                    />
                  )}
                  <Line
                    type="monotone"
                    dataKey="ly_total"
                    stroke={LY_COLOR}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    isAnimationActive={false}
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <p className="mt-2 text-token-xs text-muted-foreground">
          Revenue is net of refunds and excludes test accounts. Days are UK
          (Europe/London) days. Today is partial and is excluded from averages.{' '}
          {hasDailyForecast
            ? 'Future days carry the forecast: renewals on the day each bill is due (after churn and collection haircuts) plus modelled new business, so the renewal batch steps on its real date. The alignment toggle only moves the last-year line.'
            : 'Future days carry the forecast (dashed) following last year\u2019s within-month shape until the pipeline publishes its daily forecast.'}
        </p>
      </div>
    </div>
  );
}
